import React, { useState } from 'react'

import { useSelfActions } from 'redux/actions/Self'

import { PeriodType } from 'types/GlobalCustomer'

import { useTranslation } from 'language/translator'
import { useCustomer } from 'redux/hooks/Customer'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'

import SelectDate from 'components_new/molecules/SelectDate'
import SelectSearch from 'components_new/molecules/SelectSearch'

import dayjs from 'utils/dayjs'

type PeriodOption = {
  id: PeriodType | null
  title: string
}

const EconomyAccountingPeriod = () => {
  const { tryUpdateEconomy } = useSelfActions()
  const { t, translateEnum } = useTranslation()
  const customer = useCustomer()

  // The DateCalendar's `views` are `['year', 'month']`, so every selection
  // starts in the year view. MUI only reports the newly picked year through
  // `onYearChange` - the subsequent `onMonthChange` event still carries the
  // year of the (stale, controlled) `value` prop. We remember the picked
  // year here so it can be merged back in once the month is chosen,
  // otherwise changing only the year silently reverts to the old one.
  const [pendingYear, setPendingYear] = useState<number | null>(null)

  const maxDate = dayjs().subtract(1, 'month')
  const minDate = dayjs().subtract(4, 'year')

  if (!customer) {
    return null
  }

  const error =
    customer.period_type === PeriodType.MANUALLY &&
    !customer.latest_closed_period

  const options: PeriodOption[] = [
    ...Object.values(PeriodType).map((ptype) => ({
      id: ptype,
      title: translateEnum('period_types', ptype)
    })),
    { id: null, title: t('global', 'none') }
  ]

  return (
    <>
      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: 256
        }}
      >
        <SelectSearch
          closeOnSelect
          disableClearable={true}
          multiple={false}
          noGutter={true}
          onChange={(option) => {
            if (option) {
              tryUpdateEconomy({
                period_type: option.id
              })
            }
          }}
          options={options}
          selected={
            customer.period_type
              ? {
                  id: customer.period_type,
                  title: translateEnum('period_types', customer.period_type)
                }
              : null
          }
          size={'small'}
        />

        <Collapse
          unmountOnExit={true}
          in={customer.period_type === PeriodType.MANUALLY}
          sx={{ width: '100%' }}
        >
          <SelectDate
            DateCalendarProps={{
              maxDate: maxDate,
              minDate: minDate,
              onYearChange: (event) => {
                if (event) {
                  setPendingYear(dayjs(event).year())
                }
              },
              onMonthChange: (event) => {
                if (event) {
                  const period =
                    pendingYear !== null
                      ? dayjs(event).year(pendingYear)
                      : dayjs(event)

                  tryUpdateEconomy({
                    latest_closed_period: period.format('YYYY-MM')
                  })
                  setPendingYear(null)
                }
              },
              views: ['year', 'month']
            }}
            TextFieldProps={{
              error: error,
              helperText: error
                ? t('accounting', 'latest_closed_period_error_text')
                : null,
              label: t('accounting', 'latest_closed_period'),
              size: 'small',
              placeholder: 'Välj månad...'
            }}
            value={customer.latest_closed_period}
          />
        </Collapse>
      </Box>
    </>
  )
}

export default EconomyAccountingPeriod
